import { Controller } from "@hotwired/stimulus";
import $ from "jquery";
import "blueimp-canvas-to-blob/js/canvas-to-blob";

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = ["invalidated", "validated", "approved", "imported"];

export default class extends Controller {
  static targets = ["input", "dropzone", "uploadButton"];

  connect() {
    const name = this.data.get("name");
    const url = this.data.get("url");
    const $el = $(this.inputTarget);

    $el
      .fileupload({
        url: url,
        uploadTemplateId: name + "-template-upload",
        downloadTemplateId: name + "-template-download",
        dataType: "json",
        replaceFileInput: true,
        autoUpload: true,
        dropZone: $(this.dropzoneTarget),
        maxNumberOfFiles: 1,
        acceptFileTypes: /(\.|\/)(csv)$/i,
        maxFileSize: 25000000, // 25 MB
        formData: () => {
          const mode = document.querySelector('input[name="mode"]:checked')?.value || "create";
          return [{ name: "import_mode", value: mode }];
        },

        start: () => {
          this.hideButton();
          this._uploading = true;
          $el.fileupload("option", "dropZone", null);
        },
        stop: () => {
          this._uploading = false;
          $el.fileupload("option", "dropZone", $(this.dropzoneTarget));
        },
        destroyed: () => this.showButton(),
        destroyfailed: () => this.showButton(),
        fail: () => {
          this.showButton();
          this._uploading = false;
          $el.fileupload("option", "dropZone", $(this.dropzoneTarget));
        },
      })
      .bind("fileuploadadd", (_e, data) => {
        if (this._uploading) return false;
        if (data.originalFiles && data.originalFiles.length > 1) {
          this._showMultipleFilesError();
          return false;
        }
        this._clearMultipleFilesError();
      })
      .bind("fileuploaddone", (_e, data) => {
        const file = data.result?.files?.[0];
        if (file?.id && file?.statusUrl) {
          this._startPolling(file.id, file.statusUrl);
        }
      });

    $("#modal__import").on("show.bs.modal", () => {
      this._clearFileList();
    });
  }

  disconnect() {
    this._stopPolling();
  }

  hideButton() {
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.style.display = "none";
    }
  }

  showButton() {
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.style.display = "";
    }
  }

  // --- Private helpers ---

  _startPolling(fileId, statusUrl) {
    this._stopPolling();
    this._pollIntervalId = setInterval(() => this._pollStatus(fileId, statusUrl), POLL_INTERVAL_MS);
  }

  _stopPolling() {
    if (this._pollIntervalId) {
      clearInterval(this._pollIntervalId);
      this._pollIntervalId = null;
    }
  }

  async _pollStatus(fileId, statusUrl) {
    try {
      const response = await fetch(statusUrl, {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      });

      if (!response.ok) return;

      const { status, errors } = await response.json();

      if (!TERMINAL_STATUSES.includes(status)) return;

      this._stopPolling();

      if (status === "invalidated") {
        this._showErrors(fileId, errors);
      } else if (status === "validated") {
        await this._loadPreview(fileId);
      }
    } catch (_err) {
      // network error — keep polling
    }
  }

  _showErrors(fileId, errors) {
    const fileRow = this.element.querySelector(`[data-id="${fileId}"]`);
    if (!fileRow) return;

    const existingErrors = fileRow.querySelector(".batch-import-errors");
    if (existingErrors) existingErrors.remove();

    const processingMessage = fileRow.querySelector("#batch-import_processing-message");
    if (processingMessage) processingMessage.remove();

    const container = document.createElement("div");
    container.className = "batch-import-errors";
    (errors || []).forEach((msg) => {
      const span = document.createElement("span");
      span.className = "error text-danger input__error d-block";
      span.textContent = msg;
      container.appendChild(span);
    });

    const previewData = fileRow.querySelector(".preview__data");
    if (previewData) {
      previewData.appendChild(container);
    } else {
      fileRow.appendChild(container);
    }
  }

  async _loadPreview(fileId) {
    const baseUrl = this.data.get("url"); // /v2/batch_imports
    const previewUrl = `${baseUrl}/${fileId}/preview`;

    try {
      const response = await fetch(previewUrl, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      });

      if (!response.ok) return;

      const html = await response.text();

      // Hide upload modal
      $("#modal__import").modal("hide");

      // Remove any stale preview modal to ensure the correct delete URL is used
      const existing = document.getElementById("modal__batch-import-preview");
      if (existing) existing.remove();

      // Append to body
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html;
      document.body.appendChild(wrapper.firstElementChild);

      const $modal = $("#modal__batch-import-preview");
      $modal.modal("show");
      $modal.one("hidden.bs.modal", () => {
        $(".modal-backdrop").remove();
        $("body").removeClass("modal-open").css("padding-right", "");
      });
    } catch (_err) {
      // silently fail — user can retry
    }
  }

  _showMultipleFilesError() {
    const filesContainer = this.element.querySelector("#batch-import_files");
    if (!filesContainer) return;

    filesContainer.innerHTML = "";
    const span = document.createElement("span");
    span.className = "error text-danger input__error d-block";
    span.textContent = this.data.get("maxFilesError");
    filesContainer.appendChild(span);
  }

  _clearMultipleFilesError() {
    const filesContainer = this.element.querySelector("#batch-import_files");
    if (!filesContainer) return;

    const errorSpan = filesContainer.querySelector(".error.text-danger");
    if (errorSpan) errorSpan.remove();
  }

  _clearFileList() {
    const filesContainer = this.element.querySelector("#batch-import_files");
    if (filesContainer) filesContainer.innerHTML = "";
    this.showButton();
    this._uploading = false;
  }
}
